/**
 * Interface representing a collection of searchable fields.
 *
 * @interface ISearchableFields
 *
 * @property {ISearchableField} [field: string] - A mapping of field names to their searchable field definitions.
 */
export type ISearchableFields = Record<string, ISearchableField>;

/**
 * Interface representing a searchable field.
 *
 * @interface ISearchableField
 *
 * @property {string} name - The name of the searchable field.
 * @property {boolean} value - Indicates whether the field is currently searchable.
 * @property {'desc' | 'asc'} [sortType] - The type of sorting for the field, either descending or ascending.
 */
export interface ISearchableField {
  /** @member {string} name The name of the searchable field. */
  name: string,
  /** @member {boolean} value Indicates whether the field is currently searchable. */
  value: boolean,
  /** @member {'desc' | 'asc'} [sortType] The type of sorting for the field, either descending or ascending. */
  sortType?: 'desc' | 'asc';
}

/**
 * The default searchable fields used in the application.
 *
 * @constant {ISearchableFields}
 * @default
 *
 * @property {ISearchableField} code - The searchable field for the code.
 * @property {ISearchableField} desc - The searchable field for the description.
 * @property {ISearchableField} dep - The searchable field for the department.
 * @property {ISearchableField} subDep - The searchable field for the sub-department.
 * @property {ISearchableField} suppCode - The searchable field for the supplier stock code.
 * @property {ISearchableField} regularSuppCode - The searchable field for the regular supplier code.
 * @property {ISearchableField} binL - The searchable field for the bin location.
 * @property {ISearchableField} barcode - The searchable field for the primary barcode.
 * @property {ISearchableField} multipleBarcodes - The searchable field for additional barcodes.
 */
export const DEFAULT_SEARCHABLE_FIELDS: ISearchableFields = {
  ['code']: {value: true, name: 'Code'},
  ['desc']: {value: true, name: 'Description'},
  ['dep']: {value: true, name: 'Department'},
  ['subDep']: {value: true, name: 'Sub Department'},
  ['suppCode']: {value: true, name: 'Supplier Stock Code'},
  ['regularSuppCode']: {value: true, name: 'Regular Supplier Code'},
  ['binL']: {value: true, name: 'Bin Location'},
  ['barcode']: {value: true, name: 'Barcode'},
  ['multipleBarcodes']: {value: true, name: 'Additional Barcodes'},
};
